import { z } from "zod";
import { RAW_PAX_CREDITS_PER_DISPLAY_CREDIT } from "../../accounting/shared/constants";
import { Timestamp } from "../../common";
import { enterThresholdMessage } from "./messages";

export const OfferId = z.enum([
  "pack_small",
  "pack_medium",
  "pack_large",
  "token_basic",
  "token_regular",
  "token_pro",
  "patron",
]);
export type OfferId = z.infer<typeof OfferId>;

const currency = z.literal("usd");

/** The offer's product in the App Store, which prices it in the player's own storefront. */
const appStoreProductId = z.string().min(1);

export const PackOfferId = z.enum(["pack_small", "pack_medium", "pack_large"]);
export type PackOfferId = z.infer<typeof PackOfferId>;

export const PackOffer = z.object({
  offerId: PackOfferId,
  kind: z.literal("pack"),
  name: z.string(),
  priceCents: z.number().int().positive(),
  currency,
  creditsRaw: z.string().regex(/^[1-9][0-9]*$/),
  appStoreProductId,
});

const TokenSubscriptionOffer = z.object({
  offerId: z.enum(["token_basic", "token_regular", "token_pro"]),
  kind: z.literal("token_subscription"),
  name: z.string(),
  priceCents: z.number().int().positive(),
  currency,
  interval: z.literal("month"),
  creditsRaw: z.string().regex(/^[1-9][0-9]*$/),
  appStoreProductId,
});

const PatronOffer = z.object({
  offerId: z.literal("patron"),
  kind: z.literal("patron"),
  name: z.string(),
  priceCents: z.number().int().positive(),
  currency,
  interval: z.literal("month"),
  appStoreProductId,
});

export const Offer = z.discriminatedUnion("kind", [PackOffer, TokenSubscriptionOffer, PatronOffer]);
export type Offer = z.infer<typeof Offer>;

export const ListOffersResponse = z.object({
  offers: z.array(Offer),
});
export type ListOffersResponse = z.infer<typeof ListOffersResponse>;

/** Where on this site Stripe returns to, paid or not. A path, so it can never leave the site. */
export const ReturnPath = z
  .string()
  .max(2048)
  .regex(/^\/(?![/\\])/, "A path on this site, starting with a single /.");

/** The page on the site that Checkout returns to when the iOS app opened it. iOS opens the app for it. */
export const IOS_APP_CHECKOUT_RETURN_PATH = "/checkout/app-return";

/**
 * Where Checkout is opened. From `ios_app` it opens in Safari, lays out for a phone, and returns
 * to `IOS_APP_CHECKOUT_RETURN_PATH` instead of `returnPath`.
 */
export const CheckoutOpener = z.enum(["web", "ios_app"]);
export type CheckoutOpener = z.infer<typeof CheckoutOpener>;

export const CreateCheckoutSessionBody = z.object({
  offerId: z.string().min(1),
  returnPath: ReturnPath.optional(),
  openedFrom: CheckoutOpener.optional(),
});
export type CreateCheckoutSessionBody = z.infer<typeof CreateCheckoutSessionBody>;

export const CheckoutSessionResponse = z.object({
  checkoutSessionId: z.string(),
  url: z.url(),
});
export type CheckoutSessionResponse = z.infer<typeof CheckoutSessionResponse>;

export const CheckoutSessionId = z
  .string()
  .max(255)
  .regex(/^cs_(test|live)_[A-Za-z0-9]+$/, "A Stripe Checkout Session id.");

/**
 * `granted`: the pack's credits are in the ledger. `processing`: the credits are in the ledger
 * while a delayed payment clears, and the webhook removes them if it fails. `payment_failed`: the
 * payment failed, so nothing was granted. `expired`: the session is too old for the return link to
 * grant again. `not_paid`: the session is open or expired. `subscribed`: a plan checkout is
 * finished, and Stripe's webhook grants its month. `auto_top_up_enabled`: setup mode finished and
 * the automatic top-up rule is on (same work as the webhook, safe to call twice).
 * `auto_top_up_pending`: Stripe has not finished saving or verifying the payment method, as with a
 * bank account waiting on microdeposits. The webhook turns the rule on when verification finishes.
 * `auto_top_up_refused`: the setup can never turn the rule on, so it stays off.
 */
export const CheckoutCompletion = z.object({
  status: z.enum([
    "granted",
    "processing",
    "payment_failed",
    "expired",
    "not_paid",
    "subscribed",
    "auto_top_up_enabled",
    "auto_top_up_pending",
    "auto_top_up_refused",
  ]),
});
export type CheckoutCompletion = z.infer<typeof CheckoutCompletion>;

export const BillingPortalSessionResponse = z.object({
  url: z.url(),
});
export type BillingPortalSessionResponse = z.infer<typeof BillingPortalSessionResponse>;

/** Where a subscription was bought, and so the only place it can be changed or canceled. */
export const SubscriptionProvider = z.enum(["stripe", "app_store"]);
export type SubscriptionProvider = z.infer<typeof SubscriptionProvider>;

export const LiveSubscription = z.object({
  subscriptionId: z.string(),
  provider: SubscriptionProvider,
  offerId: z.string(),
  status: z.enum(["incomplete", "trialing", "active", "past_due", "unpaid", "paused"]),
  currentPeriodEnd: Timestamp,
  cancelAtPeriodEnd: z.boolean(),
});
export type LiveSubscription = z.infer<typeof LiveSubscription>;

/**
 * Every live subscription of each kind, the one that runs longest first. A player who bought the
 * same kind on the web and in the App Store has one from each, and changes each where it was bought.
 */
export const SubscriptionResponse = z.object({
  tokenSubscriptions: z.array(LiveSubscription),
  patronSubscriptions: z.array(LiveSubscription),
});
export type SubscriptionResponse = z.infer<typeof SubscriptionResponse>;

/**
 * Where an App Store purchase is made. `production` moves real money; the `sandbox` (TestFlight,
 * sandbox accounts, and App Review) and `xcode` make free test purchases.
 */
export const AppStoreEnvironment = z.enum(["production", "sandbox", "xcode"]);
export type AppStoreEnvironment = z.infer<typeof AppStoreEnvironment>;

export const AppStoreAccountQuery = z.object({
  /** The environment the app's StoreKit runs in, which decides whether the caller may buy. */
  environment: AppStoreEnvironment,
  /** The storefront the caller buys in, as StoreKit names it, such as `USA`. Absent when StoreKit can't tell. */
  storefront: z
    .string()
    .regex(/^[A-Z]{3}$/)
    .optional(),
});
export type AppStoreAccountQuery = z.infer<typeof AppStoreAccountQuery>;

export const AppStoreAccount = z.object({
  /** Passed to StoreKit as `appAccountToken`, so every transaction of a purchase names this player. */
  appAccountToken: z.uuid(),
  /** Why the caller cannot buy in that App Store environment, or `null` when they can. */
  paymentsClosedReason: z.string().nullable(),
  /** Whether the app may also offer Checkout on the web, at the web's prices, in that storefront. */
  webCheckoutOpen: z.boolean(),
});
export type AppStoreAccount = z.infer<typeof AppStoreAccount>;

export const AppStoreTransactionBody = z.object({
  /** The transaction as StoreKit hands it over, signed by the App Store: its `jwsRepresentation`. */
  signedTransaction: z.string().min(1).max(16_384),
});
export type AppStoreTransactionBody = z.infer<typeof AppStoreTransactionBody>;

/**
 * `granted`: what the transaction bought is in the ledger, recorded now or by an earlier report or
 * notification. `revoked`: Apple refunded or revoked it, so it grants nothing. Either way the
 * app finishes the transaction.
 */
export const AppStoreTransactionOutcome = z.object({
  status: z.enum(["granted", "revoked"]),
});
export type AppStoreTransactionOutcome = z.infer<typeof AppStoreTransactionOutcome>;

/** The most a threshold may be, in display credits. */
export const MAX_AUTO_THRESHOLD_CREDITS = 1000n;

/** Raw credits, more than 0 and at most 1,000 display credits. */
export const AutoThresholdRaw = z
  .string()
  .regex(/^[1-9][0-9]*$/)
  .refine(
    value => BigInt(value) <= MAX_AUTO_THRESHOLD_CREDITS * RAW_PAX_CREDITS_PER_DISPLAY_CREDIT,
    { message: enterThresholdMessage },
  );
export type AutoThresholdRaw = z.infer<typeof AutoThresholdRaw>;

const AutoTopUpState = z.enum(["off", "on", "charging", "needs_bank", "needs_address", "declined"]);

/**
 * How a saved payment method is shown, whatever its kind. `type` is Stripe's payment method type,
 * `label` names it (a card brand, "Link", a bank), and `detail` tells one of that kind from
 * another (card digits, an email), or is null when there is nothing to tell.
 */
export const SavedPaymentMethod = z.object({
  type: z.string(),
  label: z.string(),
  detail: z.string().nullable(),
  /** Whether Stripe may charge it while the player is away. */
  allowsAutomatic: z.boolean(),
});
export type SavedPaymentMethod = z.infer<typeof SavedPaymentMethod>;

export const TopUpSettings = z.object({
  paymentMethod: SavedPaymentMethod.nullable(),
  autoOfferId: PackOfferId.nullable(),
  autoThresholdRaw: AutoThresholdRaw.nullable(),
  autoState: AutoTopUpState,
  /** Why the caller cannot start a payment on this deployment, or `null` when they can. */
  paymentsClosedReason: z.string().nullable(),
});
export type TopUpSettings = z.infer<typeof TopUpSettings>;

export const AutoTopUpRule = z.strictObject({
  autoOfferId: PackOfferId,
  autoThresholdRaw: AutoThresholdRaw,
});
export type AutoTopUpRule = z.infer<typeof AutoTopUpRule>;

export const AutoTopUpSetupBody = z.object({
  autoOfferId: PackOfferId,
  autoThresholdRaw: AutoThresholdRaw,
  returnPath: ReturnPath.optional(),
});
export type AutoTopUpSetupBody = z.infer<typeof AutoTopUpSetupBody>;

export const AutoTopUpSetupResponse = z.object({
  url: z.url(),
});
export type AutoTopUpSetupResponse = z.infer<typeof AutoTopUpSetupResponse>;

export const SavedPaymentMethodQuoteBody = z.object({
  offerId: z.enum([...PackOfferId.options, ...TokenSubscriptionOffer.shape.offerId.options]),
});
export type SavedPaymentMethodQuoteBody = z.infer<typeof SavedPaymentMethodQuoteBody>;

const cents = z.number().int().nonnegative();

export const QuotedPrice = z.object({
  status: z.literal("quoted"),
  subtotalCents: cents,
  taxCents: cents,
  totalCents: cents,
});
export type QuotedPrice = z.infer<typeof QuotedPrice>;

export const SavedPaymentMethodQuote = z.discriminatedUnion("status", [
  QuotedPrice,
  /** Stripe has no address to tax against yet. */
  z.object({ status: z.literal("needs_checkout") }),
]);
export type SavedPaymentMethodQuote = z.infer<typeof SavedPaymentMethodQuote>;

export const SavedPaymentMethodPaymentBody = SavedPaymentMethodQuoteBody.extend({
  /** The total the player was shown. The payment method is charged exactly this, or nothing. */
  expectedTotalCents: cents,
  /** Made by the client when the confirm popup opens. Sending it again never charges twice. */
  purchaseAttemptId: z.uuid(),
  /** Where the Checkout offered instead returns to, when the payment method cannot be charged here. */
  returnPath: ReturnPath.optional(),
});
export type SavedPaymentMethodPaymentBody = z.infer<typeof SavedPaymentMethodPaymentBody>;

export const SavedPaymentMethodPaymentResponse = z.discriminatedUnion("status", [
  z.object({ status: z.literal("succeeded") }),
  /** The credits are added while the payment clears, and removed if it fails. */
  z.object({ status: z.literal("processing") }),
  z.object({ status: z.literal("price_changed"), quote: QuotedPrice }),
  z.object({
    status: z.literal("needs_checkout"),
    reason: z.enum(["bank_check", "address"]),
    url: z.url(),
  }),
  z.object({ status: z.literal("declined") }),
  z.object({ status: z.literal("pending") }),
]);
export type SavedPaymentMethodPaymentResponse = z.infer<typeof SavedPaymentMethodPaymentResponse>;
